import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'tailor' | 'customer' | 'admin' | ('tailor' | 'customer' | 'admin')[];
  allowPendingRole?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole, allowPendingRole = false }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Allow pending role if explicitly allowed (for OAuth completion page)
  if (allowPendingRole && user?.role === 'pending') {
    return <>{children}</>;
  }

  if (requiredRole) {
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!allowedRoles.includes(user?.role as any)) {
      // If user has pending role and not on completion page, redirect to completion
      if (user?.role === 'pending') {
        return <Navigate to="/auth/complete-oauth" replace />;
      }
      // Redirect to appropriate dashboard based on user role
      let redirectPath = '/customer/dashboard';
      if (user?.role === 'tailor') {
        redirectPath = '/business/dashboard';
      } else if (user?.role === 'admin') {
        redirectPath = '/admin/dashboard';
      }
      return <Navigate to={redirectPath} replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute; 